import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation, useLocale } from "./contexts/LocaleContext";
import {
  CreditCard,
  Lock,
  Gift,
  Copy,
  Check,
  Loader2,
  Clock,
  Settings,
  Users,
  Building2,
  ExternalLink,
} from "lucide-react";
import {
  createStripeCheckoutSession,
  createStripePortalSession,
} from "./config/api";
import { getUserData, setUserData } from "./utils/auth";
import { authenticatedFetch } from "./config/api";
import type { User } from "./types";
import SubscriptionPlanCards from "./components/SubscriptionPlanCards";
import SaveButton from "./components/SaveButton";

// Mock data structure for affiliate users
type AffiliateUser = {
  id: string;
  email: string;
  signupDate: string;
  subscriptionType: string;
  revenue: string;
  yourRevenue: string;
  status: string;
};

// Mock affiliate data
const mockAffiliateUsers: AffiliateUser[] = [
  {
    id: "1",
    email: "user1@example.com",
    signupDate: "2024-01-15",
    subscriptionType: "Monthly",
    revenue: "€60",
    yourRevenue: "€6",
    status: "Active",
  },
  {
    id: "2",
    email: "user2@example.com",
    signupDate: "2024-02-20",
    subscriptionType: "Yearly",
    revenue: "€600",
    yourRevenue: "€60",
    status: "Active",
  },
  {
    id: "3",
    email: "user3@example.com",
    signupDate: "2024-03-10",
    subscriptionType: "Monthly",
    revenue: "€30",
    yourRevenue: "€3",
    status: "Pending",
  },
];

export default function UserSettings() {
  const t = useTranslation();
  const { locale } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [profile, setProfile] = useState<User | null>(() => getUserData());
  const [appSettings, setAppSettings] = useState<{
    defaultLanguage: string;
    notificationEmailHandle: string;
    notificationEmailName: string;
  }>({
    defaultLanguage: "cs",
    notificationEmailHandle: "",
    notificationEmailName: "",
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  // Referral state
  const [referralData, setReferralData] = useState<{
    referralCode: string;
    referredUsers: any[];
    bankInfo: any;
  } | null>(null);
  const [isSavingBankInfo, setIsSavingBankInfo] = useState(false);
  const [bankInfoMessage, setBankInfoMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    // Always refresh profile on mount so subscription status is up-to-date
    (async () => {
      try {
        const [profileRes, settingsRes, referralRes] = await Promise.all([
          authenticatedFetch("/api/user/profile"),
          authenticatedFetch("/api/settings"),
          authenticatedFetch("/api/referrals"),
        ]);

        if (profileRes.ok) {
          const data = await profileRes.json();
          setProfile(data);
          setUserData(data);
        }

        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setAppSettings({
            defaultLanguage: data.defaultLanguage || "cs",
            notificationEmailHandle: data.notificationEmailHandle || "",
            notificationEmailName: data.notificationEmailName || "",
          });
        }

        if (referralRes.ok) {
          const data = await referralRes.json();
          setReferralData(data);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    })();

    const params = new URLSearchParams(location.search);
    const success = params.get("success");
    const plan = params.get("plan") as "monthly" | "yearly" | "lifetime" | null;

    if (success === "true") {
      setMessage({
        type: "success",
        text: t.userSettings.paymentMessage.success,
      });
      // After successful payment redirect to invoice list (same locale prefix)
      const parts = location.pathname.split("/").filter(Boolean);
      const maybeLocale = parts[0];
      const localePrefix =
        maybeLocale &&
        ["en", "ru", "cs", "sk", "uk", "de"].includes(maybeLocale)
          ? `/${maybeLocale}`
          : "";

      // Wait 3 seconds so the user can see the localized success message
      setTimeout(() => {
        navigate(`${localePrefix}/invoice-list`, { replace: true });
      }, 3000);
    } else if (success === "false") {
      setMessage({
        type: "error",
        text: t.userSettings.paymentMessage.cancelled,
      });
      navigate(location.pathname, { replace: true });
    } else if (plan) {
      handlePurchase(plan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.search,
    navigate,
    location.pathname,
    t.userSettings.paymentMessage.cancelled,
    t.userSettings.paymentMessage.success,
  ]);

  const handlePurchase = async (plan: "monthly" | "yearly" | "lifetime") => {
    setIsLoading(true);
    setMessage(null);
    try {
      const parts = location.pathname.split("/").filter(Boolean);
      const maybeLocale = parts[0];
      const localePrefix =
        maybeLocale &&
        ["en", "ru", "cs", "sk", "uk", "de"].includes(maybeLocale)
          ? `/${maybeLocale}`
          : "";

      const successPath = `${localePrefix}/payment-success`;
      const cancelPath = `${localePrefix}/settings?success=false`;

      const { url } = await createStripeCheckoutSession(plan, {
        successPath,
        cancelPath,
      });
      window.location.href = url;
    } catch (error: any) {
      console.error("Error creating stripe checkout session:", error);
      setMessage({
        type: "error",
        text:
          error.message ||
          "Failed to initiate checkout. Please try again later.",
      });
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsLoading(true);
    setMessage(null);
    try {
      const { url } = await createStripePortalSession();
      window.location.href = url;
    } catch (error: any) {
      console.error("Error creating customer portal session:", error);
      setMessage({
        type: "error",
        text:
          error.message ||
          "Failed to open subscription management. Please try again later.",
      });
      setIsLoading(false);
    }
  };

  const handleCopyPromoCode = async () => {
    if (!referralData?.referralCode) return;
    try {
      const baseUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:3000' 
        : 'https://invoitio.com';
      const referralUrl = `${baseUrl}/signup?ref=${referralData.referralCode}`;
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleSaveBankInfo = async () => {
    if (!referralData) return;
    
    // Validation: Account Number OR IBAN must be present
    if (!referralData.bankInfo?.accountNumber?.trim() && !referralData.bankInfo?.iban?.trim()) {
      setBankInfoMessage({
        type: "error",
        text: t.userSettings.affiliate.bankInfo.errorRequired,
      });
      return;
    }

    setIsSavingBankInfo(true);
    setBankInfoMessage(null);
    try {
      const response = await authenticatedFetch("/api/referrals/bank-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(referralData.bankInfo),
      });

      if (response.ok) {
        setBankInfoMessage({
          type: "success",
          text: t.userSettings.affiliate.bankInfo.saved,
        });
        // Clear success message after 5 seconds
        setTimeout(() => setBankInfoMessage(null), 5000);
      } else {
        throw new Error("Failed to save bank info");
      }
    } catch (error: any) {
      setBankInfoMessage({
        type: "error",
        text: error.message || "Failed to update bank info.",
      });
    } finally {
      setIsSavingBankInfo(false);
    }
  };

  const handleChangePassword = async () => {
    if (!profile?.email) return;
    setIsLoading(true);
    setMessage(null);
    try {
      const response = await fetch("/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: profile.email, language: locale }),
      });

      if (response.ok) {
        setMessage({
          type: "success",
          text: t.userSettings.changePassword.resetLinkSent,
        });
      } else {
        const data = await response.json();
        throw new Error(data.error || "Failed to send reset link");
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text:
          error.message || "Failed to process request. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setIsSavingSettings(true);
    setSettingsError(null);
    setMessage(null);
    try {
      const response = await authenticatedFetch("/api/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          notificationEmailName: appSettings.notificationEmailName,
          notificationEmailHandle: appSettings.notificationEmailHandle,
        }),
      });

      if (response.ok) {
        setMessage({
          type: "success",
          text: t.invoiceList.settings.saved,
        });
      } else {
        const data = await response.json();
        if (data.error === "FORBIDDEN_HANDLE") {
          setSettingsError(t.userSettings.general.notificationEmailHandleErrorForbidden);
        } else if (data.error === "HANDLE_TAKEN") {
          setSettingsError(t.userSettings.general.notificationEmailHandleErrorTaken);
        } else {
          throw new Error(data.error || "Failed to save settings");
        }
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "Failed to update settings.",
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleUpdateSetting = async (key: string, value: string) => {
    setIsSavingSettings(true);
    try {
      const response = await authenticatedFetch("/api/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: value }),
      });

      if (response.ok) {
        setAppSettings((prev) => ({ ...prev, [key]: value }));

        if (key === "defaultLanguage") {
          const currentData = getUserData();
          if (currentData) {
            setUserData({
              ...currentData,
              language: value,
              preferredLanguage: value,
            });
          }
        }

        setMessage({
          type: "success",
          text: t.invoiceList.settings.saved,
        });
      } else {
        throw new Error("Failed to save setting");
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "Failed to update settings.",
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleUpdateLanguage = (newLang: string) =>
    handleUpdateSetting("defaultLanguage", newLang);

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-8 transition-colors">
          {t.userSettings.title}
        </h1>

        <div className="space-y-6">
          {message && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`p-4 rounded-xl border ${
                message.type === "success"
                  ? "bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300"
                  : "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300"
              }`}
            >
              {message.text}
            </motion.div>
          )}

          {/* Subscription Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 shadow-sm transition-colors"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg transition-colors">
                <CreditCard className="w-5 h-5 text-green-600 dark:text-green-400 transition-colors" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 transition-colors">
                  {t.userSettings.subscription.title}
                </h2>
              </div>
            </div>

            {profile?.hasActiveSubscription ? (
              <div className="space-y-6">
                <div className="mt-4 overflow-hidden rounded-[2.2rem] border border-emerald-500/20 dark:border-emerald-500/10 bg-emerald-50/30 dark:bg-emerald-500/5 p-1 transition-all duration-300">
                  <div className="bg-white dark:bg-slate-900 rounded-[1.9rem] p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center gap-5">
                      <div className="relative">
                        <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 shadow-inner">
                          <Check size={28} strokeWidth={3} />
                        </div>
                        {!profile.cancelAtPeriodEnd && (
                          <div className="absolute -top-1 -right-1 flex h-4 w-4">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500 border-2 border-white dark:border-slate-900 shadow-sm"></span>
                          </div>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                            {t.userSettings.subscriptionDetails.status}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                              profile.cancelAtPeriodEnd
                                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                            }`}
                          >
                            {profile.cancelAtPeriodEnd
                              ? t.userSettings.subscriptionDetails.canceled
                              : profile.status || "active"}
                          </span>
                        </div>
                        <div className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight leading-none">
                          {profile.plan === "yearly"
                            ? t.userSettings.subscriptionUi.yearly
                            : profile.plan === "monthly"
                            ? t.userSettings.subscriptionUi.monthly
                            : profile.plan === "lifetime"
                            ? t.userSettings.subscriptionUi.lifetime
                            : t.userSettings.subscriptionDetails.status}{" "}
                          {t.userSettings.subscriptionUi.planLabel || "Plan"}
                        </div>
                        {profile.current_period_end && (
                          <div className="text-[11px] font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                            <Clock size={12} className="text-slate-400" />
                            <span>
                              {profile.cancelAtPeriodEnd
                                ? t.userSettings.subscriptionDetails.accessUntil
                                : t.userSettings.subscriptionDetails.renewsAt}
                              :{" "}
                              {new Date(
                                profile.current_period_end
                              ).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {!profile.cancelAtPeriodEnd &&
                      profile.plan !== "lifetime" && (
                        <button
                          onClick={handleCancelSubscription}
                          disabled={isLoading || !profile.customer}
                          className="flex items-center justify-center gap-2 rounded-2xl px-6 py-3 bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/20 dark:hover:text-rose-400 transition-all duration-300 font-black text-[11px] uppercase tracking-widest disabled:opacity-50 border border-slate-200/50 dark:border-slate-700/50 hover:border-rose-200 dark:hover:border-rose-900/50 shadow-sm"
                        >
                          {isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : null}
                          <span>{t.userSettings.subscriptionUi.cancel}</span>
                        </button>
                      )}
                  </div>
                </div>

                {profile.cancelAtPeriodEnd && (
                  <div className="mt-8 space-y-4">
                    <div className="text-center">
                      <h3 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">
                        {t.userSettings.subscriptionDetails.resubscribeTitle}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {t.userSettings.subscriptionDetails.resubscribeSubtitle}
                      </p>
                    </div>
                    <SubscriptionPlanCards
                      onSubscribe={(p) => handlePurchase(p)}
                      isLoading={isLoading}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-4">
                <SubscriptionPlanCards
                  onSubscribe={(p) => handlePurchase(p)}
                  isLoading={isLoading}
                />
              </div>
            )}
          </motion.div>

          {/* Affiliate Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 shadow-sm transition-colors"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg transition-colors">
                <Gift className="w-5 h-5 text-amber-600 dark:text-amber-400 transition-colors" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 transition-colors">
                  {t.userSettings.affiliate.title}
                </h2>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <p className="text-sm text-slate-900 dark:text-slate-100 mb-4 font-bold">
                  {t.userSettings.affiliate.description}
                </p>

                <div className="max-w-md">
                  <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1 mb-1.5">
                    {t.userSettings.affiliate.yourReferralCode}
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl font-mono text-[11px] font-bold text-slate-500 dark:text-slate-400 truncate">
                      {window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://invoitio.com'}/signup?ref={referralData?.referralCode || "..."}
                    </div>
                    <button
                      onClick={handleCopyPromoCode}
                      className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-bold text-xs shadow-sm flex-shrink-0"
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied ? t.userSettings.affiliate.copied : t.userSettings.affiliate.copy}
                    </button>
                  </div>
                </div>
              </div>

              {/* Referred Users Table */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Users size={16} className="text-slate-400" />
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-widest">
                    {t.userSettings.affiliate.referredUsersTitle}
                  </h3>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <th className="py-3 px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.userSettings.affiliate.table.email}</th>
                        <th className="py-3 px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.userSettings.affiliate.table.signupDate}</th>
                        <th className="py-3 px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">{t.userSettings.affiliate.table.status}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referralData?.referredUsers && referralData.referredUsers.length > 0 ? (
                        referralData.referredUsers.map((user) => (
                          <tr key={user.id} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors">
                            <td className="py-3 px-2 text-xs font-bold text-slate-700 dark:text-slate-300">{user.email}</td>
                            <td className="py-3 px-2 text-xs text-slate-500 dark:text-slate-400">
                              {new Date(user.createdAt).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-2 text-right">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                user.isActive 
                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                  : user.isTrial
                                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                    : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-500"
                              }`}>
                                {user.isActive 
                                  ? t.userSettings.affiliate.table.active 
                                  : user.isTrial 
                                    ? t.userSettings.affiliate.table.trial 
                                    : t.userSettings.affiliate.table.inactive}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="py-8 text-center text-xs text-slate-400 font-medium italic">
                            No referrals yet. Share your code to start earning!
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bank Info Section */}
              <div className="pt-8 border-t border-slate-100 dark:border-slate-800">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-slate-400" />
                    <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-widest">
                      {t.userSettings.affiliate.bankInfo.title}
                    </h3>
                  </div>
                  <SaveButton
                    onClick={handleSaveBankInfo}
                    isSaving={isSavingBankInfo}
                    text={t.userSettings.affiliate.bankInfo.save}
                    className="!text-xs !px-4 !py-2"
                  />
                </div>

                {bankInfoMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mb-6 p-3 rounded-xl border text-xs font-bold ${
                      bankInfoMessage.type === "success"
                        ? "bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300"
                        : "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300"
                    }`}
                  >
                    {bankInfoMessage.text}
                  </motion.div>
                )}

                <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">
                  {t.userSettings.affiliate.bankInfo.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                      {t.userSettings.affiliate.bankInfo.fullName}
                    </label>
                    <input
                      type="text"
                      value={referralData?.bankInfo?.fullName || ""}
                      onChange={(e) => setReferralData(prev => prev ? ({
                        ...prev,
                        bankInfo: { ...prev.bankInfo, fullName: e.target.value }
                      }) : null)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-900 dark:text-slate-100 outline-none"
                      placeholder="e.g. John Doe"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                      {t.userSettings.affiliate.bankInfo.accountNumber}
                    </label>
                    <input
                      type="text"
                      value={referralData?.bankInfo?.accountNumber || ""}
                      onChange={(e) => setReferralData(prev => prev ? ({
                        ...prev,
                        bankInfo: { ...prev.bankInfo, accountNumber: e.target.value }
                      }) : null)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-900 dark:text-slate-100 outline-none"
                      placeholder="e.g. 123456789/0100"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                      {t.userSettings.affiliate.bankInfo.iban}
                    </label>
                    <input
                      type="text"
                      value={referralData?.bankInfo?.iban || ""}
                      onChange={(e) => setReferralData(prev => prev ? ({
                        ...prev,
                        bankInfo: { ...prev.bankInfo, iban: e.target.value }
                      }) : null)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-900 dark:text-slate-100 outline-none"
                      placeholder="CZ..."
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                      {t.userSettings.affiliate.bankInfo.bic} / {t.userSettings.affiliate.bankInfo.swift}
                    </label>
                    <input
                      type="text"
                      value={referralData?.bankInfo?.bic || ""}
                      onChange={(e) => setReferralData(prev => prev ? ({
                        ...prev,
                        bankInfo: { ...prev.bankInfo, bic: e.target.value, swift: e.target.value }
                      }) : null)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-900 dark:text-slate-100 outline-none"
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                      {t.userSettings.affiliate.bankInfo.bankName}
                    </label>
                    <input
                      type="text"
                      value={referralData?.bankInfo?.bankName || ""}
                      onChange={(e) => setReferralData(prev => prev ? ({
                        ...prev,
                        bankInfo: { ...prev.bankInfo, bankName: e.target.value }
                      }) : null)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-900 dark:text-slate-100 outline-none"
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2 lg:col-span-2">
                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                      {t.userSettings.affiliate.bankInfo.bankAddress}
                    </label>
                    <input
                      type="text"
                      value={referralData?.bankInfo?.bankAddress || ""}
                      onChange={(e) => setReferralData(prev => prev ? ({
                        ...prev,
                        bankInfo: { ...prev.bankInfo, bankAddress: e.target.value }
                      }) : null)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-900 dark:text-slate-100 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* General Settings Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 shadow-sm transition-colors"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-slate-100 dark:bg-slate-900/30 rounded-lg transition-colors">
                <Settings className="w-5 h-5 text-slate-600 dark:text-slate-400 transition-colors" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 transition-colors">
                  {t.userSettings.general.title}
                </h2>
              </div>
            </div>

            <div className="max-w-xs">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t.userSettings.general.defaultLanguage}
              </label>
              <div className="relative">
                <select
                  value={appSettings?.defaultLanguage || "cs"}
                  onChange={(e) => handleUpdateLanguage(e.target.value)}
                  disabled={isSavingSettings}
                  className="w-full pl-4 pr-10 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all appearance-none text-slate-900 dark:text-slate-100 disabled:opacity-50"
                >
                  <option value="cs">Čeština</option>
                  <option value="en">English</option>
                  <option value="ru">Русский</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  {isSavingSettings ? (
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  ) : (
                    <svg
                      className="w-4 h-4 text-slate-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {t.userSettings.general.defaultLanguageDescription}
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-slate-100 dark:border-slate-800">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-widest">
                  {t.userSettings.general.notificationEmail}
                </h3>
                <SaveButton
                  onClick={handleSaveEmailSettings}
                  isSaving={isSavingSettings}
                  disabled={
                    !appSettings.notificationEmailName.trim() ||
                    !appSettings.notificationEmailHandle.trim()
                  }
                  text={t.common.save || "Save"}
                  className="!text-xs !px-4 !py-2"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                      {t.userSettings.general.notificationEmailName}
                    </label>
                    <input
                      type="text"
                      value={appSettings.notificationEmailName}
                      onChange={(e) =>
                        setAppSettings((prev) => ({
                          ...prev,
                          notificationEmailName: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-900 dark:text-slate-100 outline-none"
                      placeholder="e.g. Alice"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                      {t.userSettings.general.notificationEmailHandle}
                    </label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={appSettings.notificationEmailHandle}
                        onChange={(e) =>
                          setAppSettings((prev) => ({
                            ...prev,
                            notificationEmailHandle: e.target.value,
                          }))
                        }
                        className={`flex-1 min-w-0 px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border ${
                          settingsError ? "border-red-500" : "border-slate-200 dark:border-slate-700"
                        } rounded-l-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-900 dark:text-slate-100 outline-none`}
                        placeholder="e.g. alice"
                      />
                      <span className="px-3 py-2 text-[10px] bg-slate-100 dark:bg-slate-800 border border-l-0 border-slate-200 dark:border-slate-700 rounded-r-xl text-slate-500 font-bold">
                        @invoitio.com
                      </span>
                    </div>
                  </div>

                  {settingsError && (
                    <div className="sm:col-span-2 px-1">
                      <p className="text-[10px] font-bold text-red-500 dark:text-red-400">
                        {settingsError}
                      </p>
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 border border-slate-100 dark:border-slate-800">
                  <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] mb-3">
                    {t.userSettings.general.notificationEmailPreview}
                  </p>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-bold text-slate-900 dark:text-slate-100">
                      {appSettings.notificationEmailName || "Name"}
                    </span>
                    <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
                      {(
                        appSettings.notificationEmailHandle || "handle"
                      ).toLowerCase()}
                      @invoitio.com
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Change Password Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
            className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 shadow-sm transition-colors"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg transition-colors">
                <Lock className="w-5 h-5 text-indigo-600 dark:text-indigo-400 transition-colors" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 transition-colors">
                  {t.userSettings.changePassword.title}
                </h2>
              </div>
            </div>
            <button
              onClick={handleChangePassword}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-500 transition-colors font-medium disabled:opacity-50"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t.userSettings.changePassword.button}
            </button>
          </motion.div>

          {/* Email Transfer Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 transition-colors"
          >
            <p className="text-sm text-blue-800 dark:text-blue-300 transition-colors">
              {t.userSettings.emailTransferNotice}
            </p>
          </motion.div>

          {/* Account Deletion Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.25 }}
            className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700"
          >
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center transition-colors">
              {t.userSettings.deleteAccountNotice}
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
